import { useToast } from '@shared/lib/ui/components-alpha';
import { formatError } from '@shared/lib/utils';
import {
  useMutationArchiveWorkspace,
  useMutationDeleteWorkspace,
  useMutationUnarchiveWorkspace,
  useMutationUndeleteWorkspace,
} from '@shared/services/authz/authz.hooks';

export type WorkspaceMutations = {
  deleteWorkspace: (wsName: string) => Promise<any>;
  undeleteWorkspace: (wsName: string) => Promise<any>;
  archiveWorkspace: (wsName: string) => Promise<any>;
  unarchiveWorkspace: (wsName: string) => Promise<any>;
};

/**
 * Hook that provides workspace mutation operations (delete, undelete, archive, unarchive)
 * Handles all mutation logic, error handling, and toast notifications
 */
export const useMutateWorkspace = (): WorkspaceMutations => {
  const { addToast, removeAllToasts } = useToast();

  const undeleteWorkspaceMutation = useMutationUndeleteWorkspace({
    onSuccess: () => {
      removeAllToasts();
    },
    onError: (err) => {
      const message = `Failed to undelete workspace: ${formatError(err)}`;
      console.error(message);
      addToast({ type: 'error', message });
    },
  });

  const deleteWorkspaceMutation = useMutationDeleteWorkspace({
    onError: (err) => {
      const message = `Failed to delete workspace: ${formatError(err)}`;
      console.error(message);
      addToast({ type: 'error', message });
    },
    onSuccess: (_data, wsName) => {
      addToast({
        type: 'success',
        message: 'Workspace successfully deleted.',
        timeout: 7000,
        actions: [
          {
            label: 'Undo',
            onClick: () => undeleteWorkspaceMutation.mutate(wsName),
          },
        ],
      });
    },
  });

  const archiveWorkspaceMutation = useMutationArchiveWorkspace({
    onError: (err) => {
      const message = `Failed to archive workspace: ${formatError(err)}`;
      console.error(message);
      addToast({ type: 'error', message });
    },
    onSuccess: () => {
      addToast({
        type: 'success',
        message: 'Workspace archived successfully.',
      });
    },
  });

  const unarchiveWorkspaceMutation = useMutationUnarchiveWorkspace({
    onError: (err) => {
      const message = `Failed to unarchive workspace: ${formatError(err)}`;
      console.error(message);
      addToast({ type: 'error', message });
    },
    onSuccess: () => {
      addToast({
        type: 'success',
        message: 'Workspace restored successfully.',
      });
    },
  });

  return {
    deleteWorkspace: deleteWorkspaceMutation.mutateAsync,
    undeleteWorkspace: undeleteWorkspaceMutation.mutateAsync,
    archiveWorkspace: archiveWorkspaceMutation.mutateAsync,
    unarchiveWorkspace: unarchiveWorkspaceMutation.mutateAsync,
  };
};
